/**
 * F1 — Helpers Mon programme
 * F1.1b : règle de gain (modes A/B) → montantPourUnPoint
 * F1.2  : (historique) helpers reward legacy — F2.2c : plus d’édition F1
 * F2.2c : résumé / aperçu alimentés par rewardMirror (GET catalogue)
 * Ne modifie pas pointsCalculator ni le moteur redeem.
 */

export const GAIN_DECIMALS = 6;
export const MODE_A_EPSILON = 1e-6;
export const EXAMPLE_ORDER_EUR = 20;
/** Scénario aperçu client (fictif, fixe) */
export const PREVIEW_ORDER_EUR = 26;
export const PREVIEW_BALANCE_BEFORE = 96;

/** Seuils historiques : masqués F1, préservés via baseline (jamais dans le patch UI) */
export const SEUIL_KEYS_HIDDEN_FROM_UI = [
  'seuilVIP',
  'seuilFidele',
  'joursNouveau',
  'seuilActif',
];

const PRISMA_META_KEYS = new Set(['id', 'commercantId', 'createdAt', 'updatedAt']);

export function roundMontantPourUnPoint(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** GAIN_DECIMALS;
  return Math.round(n * factor) / factor;
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: number } | { ok: false, reason: string }}
 */
export function parsePositiveNumber(raw) {
  if (raw === '' || raw === null || raw === undefined) {
    return { ok: false, reason: 'empty' };
  }
  const n =
    typeof raw === 'number'
      ? raw
      : Number(String(raw).trim().replace(',', '.'));
  if (!Number.isFinite(n)) return { ok: false, reason: 'nan' };
  if (n < 0) return { ok: false, reason: 'negative' };
  if (n === 0) return { ok: false, reason: 'zero' };
  return { ok: true, value: n };
}

/** MODE A : 1 € → N points → m = 1/N */
export function montantFromModeA(nPoints) {
  const parsed = parsePositiveNumber(nPoints);
  if (!parsed.ok) return parsed;
  if (parsed.value < 1) return { ok: false, reason: 'min' };
  return {
    ok: true,
    montantPourUnPoint: roundMontantPourUnPoint(1 / parsed.value),
  };
}

/** MODE B : M € → 1 point → m = M */
export function montantFromModeB(euros) {
  const parsed = parsePositiveNumber(euros);
  if (!parsed.ok) return parsed;
  if (parsed.value < 1) return { ok: false, reason: 'min' };
  return {
    ok: true,
    montantPourUnPoint: roundMontantPourUnPoint(parsed.value),
  };
}

/**
 * Détection mode au chargement (tolérance MODE_A_EPSILON).
 * @returns {{ mode: 'A'|'B', euros: number, points: number }}
 */
export function detectGainMode(montantPourUnPoint) {
  const m = Number(montantPourUnPoint);
  if (!Number.isFinite(m) || m <= 0) {
    return { mode: 'B', euros: 1, points: 1 };
  }
  const inv = 1 / m;
  const nearest = Math.round(inv);
  if (nearest >= 1 && Math.abs(inv - nearest) <= MODE_A_EPSILON) {
    return { mode: 'A', euros: 1, points: nearest };
  }
  return {
    mode: 'B',
    euros: roundMontantPourUnPoint(m),
    points: 1,
  };
}

export function formatEuroDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  const rounded = roundMontantPourUnPoint(n);
  return String(rounded).replace(/\.?0+$/, '') || String(rounded);
}

export function formatGainPhrase(mode, { euros, points }) {
  if (mode === 'A') {
    const n = Number(points);
    const label = n > 1 ? 'points' : 'point';
    return `Pour 1 € dépensé, le client gagne ${n} ${label}.`;
  }
  const m = Number(euros);
  const mLabel = formatEuroDisplay(m);
  const depense = m > 1 ? 'dépensés' : 'dépensé';
  return `Pour ${mLabel} € ${depense}, le client gagne 1 point.`;
}

/** Même formule que pointsCalculator : Math.ceil(montant / m) */
export function examplePointsForOrder(orderEur, montantPourUnPoint) {
  const m = Number(montantPourUnPoint);
  const order = Number(orderEur);
  if (!Number.isFinite(m) || m <= 0 || !Number.isFinite(order) || order < 0) {
    return null;
  }
  return Math.ceil(order / m);
}

export function formatDynamicExample(montantPourUnPoint, orderEur = EXAMPLE_ORDER_EUR) {
  const pts = examplePointsForOrder(orderEur, montantPourUnPoint);
  if (pts === null) return '';
  const label = pts > 1 ? 'points' : 'point';
  return `Exemple : ${formatEuroDisplay(orderEur)} € de commande ≈ ${pts} ${label}`;
}

/**
 * Payload PUT sûr : snapshot métier + patch F1.1b, sans métadonnées Prisma.
 * @param {Record<string, unknown>} baselineConfig
 * @param {Record<string, unknown>} patch
 */
export function buildSafeProgrammePutPayload(baselineConfig, patch) {
  const payload = {};
  const baseline = baselineConfig && typeof baselineConfig === 'object' ? baselineConfig : {};

  for (const [key, value] of Object.entries(baseline)) {
    if (PRISMA_META_KEYS.has(key)) continue;
    payload[key] = value;
  }

  const safePatch = patch && typeof patch === 'object' ? patch : {};
  for (const [key, value] of Object.entries(safePatch)) {
    if (PRISMA_META_KEYS.has(key)) continue;
    payload[key] = value;
  }

  return payload;
}

export function validationMessage(reason) {
  switch (reason) {
    case 'empty':
      return 'Veuillez renseigner une valeur.';
    case 'nan':
      return 'La valeur doit être un nombre.';
    case 'zero':
      return 'La valeur doit être supérieure à 0.';
    case 'negative':
      return 'La valeur ne peut pas être négative.';
    case 'min':
      return 'La valeur doit être au moins égale à 1.';
    case 'partial':
      return 'Indiquez à la fois les points nécessaires et le montant offert.';
    case 'not_integer':
      return 'Le nombre de points doit être un entier.';
    default:
      return 'Valeur invalide.';
  }
}

/** F1.2 — une récompense est configurée si les deux champs métier sont présents et > 0 */
export function isRewardConfigured(pointsPourReduction, valeurReduction) {
  const pts = Number(pointsPourReduction);
  const euros = Number(valeurReduction);
  return Number.isFinite(pts) && pts > 0 && Number.isFinite(euros) && euros > 0;
}

/**
 * Hydratation UI depuis le GET / PUT.
 * @returns {{ enabled: boolean, points: string, euros: string }}
 */
export function detectRewardFromConfig(programme) {
  if (!programme || !isRewardConfigured(programme.pointsPourReduction, programme.valeurReduction)) {
    return { enabled: false, points: '', euros: '' };
  }
  return {
    enabled: true,
    points: String(Math.trunc(Number(programme.pointsPourReduction))),
    euros: formatEuroInput(programme.valeurReduction),
  };
}

/** Affichage champ euros (décimales utiles, point → virgule pour saisie FR) */
export function formatEuroInput(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  return String(roundMontantPourUnPoint(n)).replace(/\.?0+$/, '') || String(n);
}

/** Affichage « 5,00 € » dans la phrase produit */
export function formatEuroRewardDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `${n.toFixed(2).replace('.', ',')} €`;
}

export function formatRewardPhrase(points, euros) {
  const pts = Number(points);
  const eur = Number(euros);
  if (!Number.isFinite(pts) || pts <= 0 || !Number.isFinite(eur) || eur <= 0) {
    return '';
  }
  const ptsLabel = pts > 1 ? 'points' : 'point';
  return `Vos clients peuvent échanger ${Math.trunc(pts)} ${ptsLabel} contre ${formatEuroRewardDisplay(eur)} offerts.`;
}

/**
 * Résout le patch F1.2 pour Enregistrer.
 * - enabled false → null / null (récompense retirée)
 * - enabled true → les deux champs obligatoires ensemble, points entiers > 0, euros > 0
 *
 * @returns {{ ok: true, pointsPourReduction: number|null, valeurReduction: number|null }
 *   | { ok: false, reason: string }}
 */
export function resolveRewardPatch({ enabled, pointsRaw, eurosRaw }) {
  if (!enabled) {
    return { ok: true, pointsPourReduction: null, valeurReduction: null };
  }

  const pointsEmpty =
    pointsRaw === '' || pointsRaw === null || pointsRaw === undefined;
  const eurosEmpty =
    eurosRaw === '' || eurosRaw === null || eurosRaw === undefined;

  if (pointsEmpty && eurosEmpty) {
    return { ok: false, reason: 'empty' };
  }
  if (pointsEmpty || eurosEmpty) {
    return { ok: false, reason: 'partial' };
  }

  const ptsParsed = parsePositiveNumber(pointsRaw);
  if (!ptsParsed.ok) return ptsParsed;
  if (!Number.isInteger(ptsParsed.value)) {
    return { ok: false, reason: 'not_integer' };
  }

  const eurosParsed = parsePositiveNumber(eurosRaw);
  if (!eurosParsed.ok) return eurosParsed;

  return {
    ok: true,
    pointsPourReduction: ptsParsed.value,
    valeurReduction: roundMontantPourUnPoint(eurosParsed.value),
  };
}

/** Phrase courte gain pour résumé (ex. « 1 € = 2 points ») — null si invalide */
export function formatGainSummaryLine(mode, { euros, points }) {
  if (mode === 'A') {
    const n = Number(points);
    if (!Number.isFinite(n) || n < 1) return null;
    const label = n > 1 ? 'points' : 'point';
    return `1 € = ${n} ${label}`;
  }
  const m = Number(euros);
  if (!Number.isFinite(m) || m < 1) return null;
  return `${formatEuroDisplay(m)} € = 1 point`;
}

/** Phrase courte récompense (ex. « 100 points = 5 € ») */
export function formatRewardSummaryLine(points, euros) {
  const pts = Number(points);
  const eur = Number(euros);
  if (!Number.isFinite(pts) || pts <= 0 || !Number.isFinite(eur) || eur <= 0) {
    return null;
  }
  const ptsLabel = pts > 1 ? 'points' : 'point';
  return `${Math.trunc(pts)} ${ptsLabel} = ${formatEuroRewardDisplay(eur)}`;
}

/**
 * Résumé miroir du formulaire (brouillon).
 * Récompenses : uniquement via rewardMirror (catalogue F2), jamais un draft F1.
 *
 * @param {{
 *   actif: boolean,
 *   gainMode: 'A'|'B',
 *   pointsPerEuro: unknown,
 *   eurosPerPoint: unknown,
 *   rewardMirror?: {
 *     status?: string,
 *     empty?: boolean,
 *     hasActiveEuro?: boolean,
 *     countLabel?: string|null,
 *     mirrorPhrase?: string|null,
 *     noActiveHint?: string|null,
 *   }|null,
 * }} draft
 */
export function buildProgrammeSummary(draft) {
  const lines = [];
  if (draft.actif) {
    lines.push('Programme actif');
  } else {
    lines.push('Programme en pause');
    lines.push('Les réglages sont conservés');
    lines.push('Aucun nouveau point n’est attribué tant que le programme reste désactivé');
  }

  let gainLine = null;
  if (draft.gainMode === 'A') {
    const resolved = montantFromModeA(draft.pointsPerEuro);
    if (resolved.ok) {
      gainLine = formatGainSummaryLine('A', {
        euros: 1,
        points: Number(String(draft.pointsPerEuro).replace(',', '.')),
      });
    }
  } else {
    const resolved = montantFromModeB(draft.eurosPerPoint);
    if (resolved.ok) {
      gainLine = formatGainSummaryLine('B', {
        euros: Number(String(draft.eurosPerPoint).replace(',', '.')),
        points: 1,
      });
    }
  }
  if (gainLine) {
    lines.push(gainLine);
  } else {
    lines.push('Règle de gain incomplète');
  }

  const mirror = draft.rewardMirror;
  if (mirror && mirror.status === 'error') {
    lines.push('Récompenses : chargement indisponible');
  } else if (!mirror || mirror.empty || mirror.status === 'loading') {
    lines.push('Les clients cumulent des points');
    lines.push('Aucune récompense n’est encore disponible');
  } else if (mirror.multiActive && mirror.multiActivePhrase) {
    if (mirror.countLabel) lines.push(mirror.countLabel);
    lines.push(mirror.multiActivePhrase);
  } else if ((mirror.exchangeable || mirror.hasActiveEuro) && mirror.mirrorPhrase) {
    if (mirror.countLabel) lines.push(mirror.countLabel);
    lines.push(`Récompense active : ${mirror.mirrorPhrase}`);
  } else {
    if (mirror.countLabel) lines.push(mirror.countLabel);
    lines.push(
      mirror.noActiveHint || 'Aucune récompense n’est actuellement échangeable.'
    );
  }

  lines.push('Points valables uniquement dans votre boutique');
  return lines;
}

/**
 * Points manquants vers la prochaine unité de récompense (solde fictif).
 * Ex. solde 148, seuil 100 → 52.
 */
export function pointsMissingForNextReward(balance, threshold) {
  const bal = Math.floor(Number(balance));
  const thr = Math.floor(Number(threshold));
  if (!Number.isFinite(bal) || bal < 0 || !Number.isFinite(thr) || thr <= 0) {
    return null;
  }
  if (bal < thr) return thr - bal;
  const rem = bal % thr;
  return rem === 0 ? 0 : thr - rem;
}

/**
 * Ratio 0–1 pour barre de progression aperçu client (présentation UI only).
 * @returns {number|null}
 */
export function previewProgressRatio(balanceAfter, targetPoints) {
  if (balanceAfter == null || targetPoints == null) return null;
  const b = Number(balanceAfter);
  const t = Number(targetPoints);
  if (!Number.isFinite(b) || b < 0 || !Number.isFinite(t) || t <= 0) return null;
  return Math.max(0, Math.min(1, b / t));
}

/** Libellé court prochaine offre catalogue (EUR ou %) — jamais inventé. */
function shortNextRewardLabel(activeReward) {
  if (!activeReward || typeof activeReward !== 'object') return null;
  const effects = Array.isArray(activeReward.effets)
    ? activeReward.effets
    : Array.isArray(activeReward.effects)
      ? activeReward.effects
      : [];
  const pct = effects.find((e) => e && e.type === 'REDUCTION_POURCENT');
  if (pct) {
    const v = Number(pct.valeur);
    if (!Number.isFinite(v) || !(v > 0) || v > 100) return null;
    const label = Number.isInteger(v) ? String(v) : String(v);
    return `${label} % de réduction`;
  }
  const eur = effects.find((e) => e && e.type === 'REDUCTION_EUR');
  const v = Number(eur?.valeur);
  if (!Number.isFinite(v) || v <= 0) return null;
  const label = Number.isInteger(v) ? String(v) : String(v);
  return `${label} € offerts`;
}

/**
 * Mini scénario aperçu client (données 100 % fictives).
 *
 * @returns {{
 *   isExample: true,
 *   nomProgramme: string,
 *   messageBienvenue: string|null,
 *   bullets: string[],
 *   orderEur: number,
 *   pointsGagnes: number|null,
 *   balanceBefore: number,
 *   balanceAfter: number|null,
 *   missingPoints: number|null,
 *   nextRewardPoints: number|null,
 *   nextRewardShortLabel: string|null,
 *   hasReward: boolean,
 *   canChooseRedeem: boolean,
 *   programmePaused: boolean,
 * }}
 */
export function buildClientPreviewScenario({
  actif,
  nomProgramme,
  messageBienvenue,
  gainMode,
  pointsPerEuro,
  eurosPerPoint,
  rewardMirror = null,
  orderEur = PREVIEW_ORDER_EUR,
  balanceBefore = PREVIEW_BALANCE_BEFORE,
}) {
  const bullets = [];
  let montant = null;
  let gainOk = false;

  if (gainMode === 'A') {
    const resolved = montantFromModeA(pointsPerEuro);
    if (resolved.ok) {
      gainOk = true;
      montant = resolved.montantPourUnPoint;
      const n = Number(String(pointsPerEuro).replace(',', '.'));
      bullets.push(
        `1 € dépensé = ${n} ${n > 1 ? 'points' : 'point'}`
      );
    }
  } else {
    const resolved = montantFromModeB(eurosPerPoint);
    if (resolved.ok) {
      gainOk = true;
      montant = resolved.montantPourUnPoint;
      const m = Number(String(eurosPerPoint).replace(',', '.'));
      bullets.push(
        `${formatEuroDisplay(m)} € dépensé${m > 1 ? 's' : ''} = 1 point`
      );
    }
  }

  bullets.push('Les points sont valables uniquement dans cette boutique.');

  // F2.2c / F2.3b / F2.5a2 — échangeable si ACTIVE € ou % ; N>1 : multi phrase
  const multiActive = Boolean(rewardMirror && rewardMirror.multiActive);
  const singleExchangeable =
    rewardMirror &&
    rewardMirror.status === 'ok' &&
    !multiActive &&
    Array.isArray(rewardMirror.activeExchangeables) &&
    rewardMirror.activeExchangeables.length === 1
      ? rewardMirror.activeExchangeables[0]
      : rewardMirror &&
          rewardMirror.status === 'ok' &&
          rewardMirror.hasActiveEuro &&
          rewardMirror.activeEuro &&
          !multiActive
        ? rewardMirror.activeEuro
        : null;
  const singleActive = Boolean(singleExchangeable);
  const hasReward = singleActive || multiActive;
  let rewardPts = null;
  let nextRewardShortLabel = null;
  if (singleActive) {
    rewardPts = Math.trunc(Number(singleExchangeable.coutPoints));
    if (!Number.isFinite(rewardPts) || rewardPts <= 0) rewardPts = null;
    nextRewardShortLabel = shortNextRewardLabel(singleExchangeable);
    if (rewardMirror.mirrorPhrase) {
      bullets.push(rewardMirror.mirrorPhrase);
    }
  } else if (multiActive && rewardMirror.multiActivePhrase) {
    bullets.push(rewardMirror.multiActivePhrase);
  }

  const pointsGagnes =
    gainOk && montant != null
      ? examplePointsForOrder(orderEur, montant)
      : null;
  const balanceAfter =
    pointsGagnes != null ? balanceBefore + pointsGagnes : null;
  const missingPoints =
    singleActive && balanceAfter != null && rewardPts != null && rewardPts > 0
      ? pointsMissingForNextReward(balanceAfter, rewardPts)
      : null;

  return {
    isExample: true,
    nomProgramme: (nomProgramme || '').trim() || 'Programme de fidélité',
    messageBienvenue: (messageBienvenue || '').trim() || null,
    bullets,
    orderEur,
    pointsGagnes,
    balanceBefore,
    balanceAfter,
    missingPoints,
    nextRewardPoints: singleActive ? rewardPts : null,
    nextRewardShortLabel: singleActive ? nextRewardShortLabel : null,
    hasReward,
    // F2.3b : multi-ACTIVE ≠ choix client déjà livré (F2.4)
    canChooseRedeem: Boolean(actif && singleActive),
    programmePaused: !actif,
  };
}

/**
 * Stats affichables F1.3 : uniquement champs fiables.
 * @returns {{ pointsActifs: number, pointsUtilises: number } | null }
 */
export function pickReliableStats(stats) {
  if (!stats || typeof stats !== 'object') return null;
  const pointsActifs = Number(stats.pointsActifs);
  const pointsUtilises = Number(stats.pointsUtilises);
  if (!Number.isFinite(pointsActifs) || !Number.isFinite(pointsUtilises)) {
    return null;
  }
  if (pointsActifs < 0 || pointsUtilises < 0) return null;
  return {
    pointsActifs: Math.trunc(pointsActifs),
    pointsUtilises: Math.trunc(pointsUtilises),
  };
}
